import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  ExpiredCodeException,
  LimitExceededException,
  NotAuthorizedException,
} from 'src/app/shared/errors/cognito/cognito-errors';
import { Routes } from '../../routes';
import { passwordValidator } from 'src/app/shared/validators/registration-validators';
import { sameValueTwoFields } from 'src/app/utils/form-validator';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-account-migration-login',
  templateUrl: './account-migration-login.component.html',
  styleUrls: ['./account-migration-login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Account Migration Login Component!
 */
export class AccountMigrationLoginComponent implements OnInit {
  form = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
    newPassword: new FormControl('', [
      Validators.required,
      passwordValidator(),
    ]),
    confirmNewpassword: new FormControl('', Validators.required),
    _notLoading: new FormControl(true, Validators.requiredTrue),
  });
  readonly email = this.form.controls.email;
  readonly password = this.form.controls.password;
  readonly newPassword = this.form.controls.newPassword;
  readonly confirmNewPassword = this.form.controls.confirmNewpassword;
  readonly labels = {
    expiredError: 'expired-account-migration-temp-passwd',
    unknownUserError: 'invalid-email-password',
    unknownError: 'unexpected-error',
    invalidEmailPassword: 'invalid-email-password',
    invalidPasswordError: 'error-invalid-password',
    wrongPasswordError: 'error-wrong-password',
    unknownPasswordError: 'error-change-password-unknown',
    limitExceededError: 'error-change-password-limit-exceeded',
    passwordChanged: 'password-successfully-changed',
  };
  loading = false;

  /**
   * @constructor
   */
  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly snackbarService: SnackBarService,
    private readonly dialogService: DialogService,
    private readonly translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.fillEmail();
    sameValueTwoFields(this.newPassword, this.confirmNewPassword, [
      Validators.required,
    ]);
  }

  /**
   * gets not loading control value
   */
  get notLoading() {
    return Boolean(this.form.controls._notLoading.value);
  }

  /**
   * @param {boolean} value
   */
  set notLoading(value: boolean) {
    this.form.controls._notLoading.setValue(value);
    this.form.controls._notLoading.updateValueAndValidity();
  }

  /**
   * fills email from route
   */
  fillEmail() {
    const email = this.activatedRoute.snapshot.queryParams['email'];
    if (email) {
      this.email.setValue(email);
      this.email.disable();
    }
  }

  /**
   * Logs in with temporary password
   */
  async login() {
    if (!this.notLoading) {
      return;
    }
    this.notLoading = false;
    const email = this.email.getRawValue();
    const password = this.password.getRawValue();
    const newPassword = this.newPassword.value;
    const confirmNewPassword = this.confirmNewPassword.value;
    if (
      !email ||
      !password ||
      !newPassword ||
      !confirmNewPassword ||
      newPassword !== confirmNewPassword
    ) {
      return;
    }
    try {
      await this.authService.changeTemporaryPassword(
        password,
        email,
        newPassword
      );
      this.router.navigate([
        `${RoutesKeys.ACCOUNT_MIGRATION}/${Routes.FINISH_ACCOUNT}`,
      ]);
    } catch (error) {
      this.handleError(error as Error);
    } finally {
      this.notLoading = true;
    }
  }

  /**
   * Opens a dialog informing what went wrong
   * @param {Error} error
   */
  private handleError(error: Error) {
    switch (error.constructor) {
      case NotAuthorizedException:
        if (error.message.startsWith('Password attempts')) {
          this.snackbarService.error(this.labels.limitExceededError);
        } else {
          this.snackbarService.error(this.labels.invalidEmailPassword);
        }
        return;
      case ExpiredCodeException:
        this.dialogService.openDialog({
          content: this.labels.expiredError,
        });
        return;
      case LimitExceededException:
        this.snackbarService.error(this.labels.limitExceededError);
        return;
      default:
        this.snackbarService.error(this.labels.unknownError);
    }
  }
}
